import { clamp01 } from "../math.js";
import { lumaOrder } from "./palette.js";
import { medianNearestSpacing } from "./select-dots.js";

/* Setup math for the lens's color drift. The 877 sunglass-lens dots never move. Instead, each
   one slowly cycles its color through a ramp of the lens's own colors, driven by a plasma of
   three sines in the shader. This file builds the ramp, each dot's home spot on it, each dot's
   random phases and amplitude, and the edge mask that keeps the lens's outline crisp while the
   inside churns. */

export interface LensPalette {
  /** The palette indices the lens dots use, each once, sorted dark to light. */
  lensPal: number[];
  lensPalN: number;
  /** Each lens dot's home rank (an index into `lensPal`), in `lensIndices` order. */
  baseRank: Int32Array;
}

/** Builds the lens's luminance ramp and each lens dot's home rank on it.
    - `colors`: the whole 22-color palette.
    - `colorIndex`: each dot's palette index, for all the data (not just the lens).
    - `lensIndices`: the lens dots' indices into all the data. */
export function buildLensPalette(
  colors: readonly string[],
  colorIndex: Uint8Array | readonly number[],
  lensIndices: Uint32Array | readonly number[],
): LensPalette {
  const used = new Set<number>();
  for (const j of lensIndices) used.add(colorIndex[j] ?? 0);
  const lensPal = lumaOrder(colors, [...used]);
  const rankOf = new Map<number, number>();
  lensPal.forEach((ci, i) => rankOf.set(ci, i));
  const baseRank = new Int32Array(lensIndices.length);
  for (let i = 0; i < lensIndices.length; i++) {
    baseRank[i] = rankOf.get(colorIndex[lensIndices[i]!] ?? 0) ?? 0;
  }
  return { lensPal, lensPalN: lensPal.length, baseRank };
}

/** Edge mask, in [0,1]. Dots on the lens's rim (few neighbors) get about 0, so the outline
    stays crisp; dots inside get 1. `xs` and `ys` are the lens dots' artwork coordinates, in
    `lensIndices` order. It's O(n²), but n is only 877. */
export function lensCoreMask(
  xs: Float32Array | readonly number[],
  ys: Float32Array | readonly number[],
): Float32Array {
  const n = xs.length;
  const core = new Float32Array(n);
  if (!n) return core;

  const med = medianNearestSpacing(xs, ys);
  const R2 = (med * 2.4) ** 2;

  const cnt = new Int32Array(n);
  for (let i = 0; i < n; i++) {
    let c = 0;
    for (let k = 0; k < n; k++) {
      if (k === i) continue;
      const dx = (xs[i] ?? 0) - (xs[k] ?? 0);
      const dy = (ys[i] ?? 0) - (ys[k] ?? 0);
      if (dx * dx + dy * dy <= R2) c++;
    }
    cnt[i] = c;
  }
  const ref = [...cnt].sort((p, q) => p - q)[Math.floor(n * 0.75)] || 1;
  for (let i = 0; i < n; i++) {
    core[i] = clamp01(((cnt[i] ?? 0) / ref - 0.78) / 0.22);
  }
  return core;
}

export interface LensDotParams {
  /** Each dot's main phase (plasma terms 1 and 3). */
  ph: Float32Array;
  /** Each dot's second, independent phase (terms 2 and 3). */
  ph2: Float32Array;
  /** Each dot's amplitude multiplier, uniform in [0.5, 1.7). */
  ampJ: Float32Array;
}

/** Random phases and amplitude for each dot, in clusters, so a color change can cover a small
    blob instead of always one dot. Neighbors get grouped (greedily) in clusters of 1 to 3 that
    share all three values, so the shader drifts them the same way and they recolor together.
    `xs` and `ys` are the lens dots' artwork coordinates, in `lensIndices` order. `rng` gives
    [0,1) and is passed in, so a seeded run always comes out the same. */
export function lensDotParams(
  rng: () => number,
  xs: Float32Array | readonly number[],
  ys: Float32Array | readonly number[],
): LensDotParams {
  const n = xs.length;
  const ph = new Float32Array(n);
  const ph2 = new Float32Array(n);
  const ampJ = new Float32Array(n);
  const assigned = new Uint8Array(n);
  for (let seed = 0; seed < n; seed++) {
    if (assigned[seed]) continue;
    const size = 1 + Math.floor(rng() * 3); // a cluster of 1 to 3 dots
    const p = rng() * 6.283;
    const p2 = rng() * 6.283;
    const a = 0.5 + rng() * 1.2;
    const sx = xs[seed] ?? 0,
      sy = ys[seed] ?? 0;
    assigned[seed] = 1;
    ph[seed] = p;
    ph2[seed] = p2;
    ampJ[seed] = a;
    // Grow the cluster by grabbing the nearest free dot, again and again.
    for (let count = 1; count < size; count++) {
      let best = -1,
        bestD = Infinity;
      for (let k = 0; k < n; k++) {
        if (assigned[k]) continue;
        const dx = (xs[k] ?? 0) - sx,
          dy = (ys[k] ?? 0) - sy;
        const d = dx * dx + dy * dy;
        if (d < bestD) {
          bestD = d;
          best = k;
        }
      }
      if (best < 0) break;
      assigned[best] = 1;
      ph[best] = p;
      ph2[best] = p2;
      ampJ[best] = a;
    }
  }
  return { ph, ph2, ampJ };
}
